import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';

const Preloader = ({ onComplete }) => {
  const [progress, setProgress] = useState(0);
  const [showSkip, setShowSkip] = useState(false);
  const [timecode, setTimecode] = useState('00:00:00:00');

  useEffect(() => {
    // Check if already visited
    const hasVisited = sessionStorage.getItem('fp_visited');
    if (hasVisited) {
      onComplete();
      return;
    }

    // Show skip button after 2 seconds
    const skipTimer = setTimeout(() => setShowSkip(true), 2000);

    // Progress animation
    const progressInterval = setInterval(() => {
      setProgress((prev) => {
        if (prev >= 100) {
          clearInterval(progressInterval);
          setTimeout(() => {
            sessionStorage.setItem('fp_visited', 'true');
            onComplete();
          }, 500);
          return 100;
        }
        return prev + 2;
      });
    }, 50);

    // Timecode animation
    let frame = 0;
    const timecodeInterval = setInterval(() => {
      frame++;
      const seconds = Math.floor(frame / 24);
      const frames = frame % 24;
      const minutes = Math.floor(seconds / 60);
      const secs = seconds % 60;
      setTimecode(
        `00:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}:${String(frames).padStart(2, '0')}`
      );
    }, 1000 / 24);

    return () => {
      clearTimeout(skipTimer);
      clearInterval(progressInterval);
      clearInterval(timecodeInterval);
    };
  }, [onComplete]);

  const handleSkip = () => {
    sessionStorage.setItem('fp_visited', 'true');
    onComplete();
  };

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-[100] preloader-bg flex items-center justify-center"
        initial={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.8 }}
      >
        {/* Gradient Orbs */}
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-primary/20 rounded-full blur-[120px]" />
        <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-secondary/15 rounded-full blur-[120px]" />

        {/* Content */}
        <div className="relative z-10 w-full max-w-lg px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            {/* Top Bar - REC indicator and Timecode */}
            <div className="flex items-center justify-between mb-12">
              <div className="flex items-center gap-3">
                <div className="rec-dot" />
                <span className="font-mono text-red-500 text-sm tracking-wider">REC</span>
              </div>
              <div className="font-mono text-white/50 text-sm tracking-wider">
                {timecode}
              </div>
            </div>

            {/* Logo */}
            <motion.div
              className="text-center mb-12"
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.3, duration: 0.8 }}
            >
              <img
                src="https://customer-assets.emergentagent.com/job_focusagency/artifacts/gtut8c09_FP%20Logo%20Straight%20White.png"
                alt="Focus Pixel"
                className="h-10 mx-auto mb-4"
              />
              <p className="text-fp-textMuted text-sm tracking-wider">
                Digital Growth Partner
              </p>
            </motion.div>

            {/* Progress Bar */}
            <div className="mb-8">
              <div className="relative h-1 bg-white/10 rounded-full overflow-hidden">
                <motion.div
                  className="h-full bg-gradient-to-r from-primary to-secondary"
                  initial={{ width: '0%' }}
                  animate={{ width: `${progress}%` }}
                  transition={{ ease: 'linear' }}
                />
              </div>
              <div className="flex justify-between mt-3">
                <span className="font-mono text-xs text-white/30">Loading</span>
                <span className="font-mono text-xs text-primary">{progress}%</span>
              </div>
            </div>

            {/* Frame indicators */}
            <div className="flex justify-center gap-8 text-white/20 font-mono text-xs">
              <span>4K</span>
              <span>24fps</span>
              <span>HDR</span>
            </div>
          </motion.div>

          {/* Skip Button */}
          <AnimatePresence>
            {showSkip && (
              <motion.button
                onClick={handleSkip}
                className="absolute -bottom-16 left-1/2 -translate-x-1/2 text-white/40 hover:text-white text-sm transition-colors"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                exit={{ opacity: 0 }}
                data-testid="skip-intro-btn"
              >
                Skip intro →
              </motion.button>
            )}
          </AnimatePresence>
        </div>

        {/* Corner Frame Markers */}
        <div className="absolute top-8 left-8 w-8 h-8 border-l-2 border-t-2 border-white/10" />
        <div className="absolute top-8 right-8 w-8 h-8 border-r-2 border-t-2 border-white/10" />
        <div className="absolute bottom-8 left-8 w-8 h-8 border-l-2 border-b-2 border-white/10" />
        <div className="absolute bottom-8 right-8 w-8 h-8 border-r-2 border-b-2 border-white/10" />
      </motion.div>
    </AnimatePresence>
  );
};

export default Preloader;
